import { Button, Field, Input, RadioGroup } from '@bb/ui';
import type { CanvasSettings } from '../../../api/client.ts';
import { AudienceBuilder, emptyAudience } from '../../audience/AudienceBuilder.tsx';
import { triggerTemplate } from '../../campaigns/trigger/actionBased.ts';
import { TriggerEditor } from '../../campaigns/trigger/TriggerEditor.tsx';
import type { WizardStepProps } from './EntryStep.tsx';
import { issuesBelow, MAX_CORRELATIONS, MAX_EXIT_EVENTS, messagesAt, type ExitEvent, type SettingsIssue } from './settings.ts';

type Exit = CanvasSettings['exit'];

const AUDIENCE_CHOICES = [
  { value: 'none', label: 'No exit audience' },
  { value: 'audience', label: 'Users who come to match an audience leave' },
];

/** «event property == entry context key» rows: only an event about this entry ends it. */
function Correlation({ event, index, issues, readOnly, onChange }: { event: ExitEvent; index: number; issues: SettingsIssue[]; readOnly: boolean; onChange: (next: ExitEvent) => void }) {
  const rows = event.correlate;
  const setRow = (j: number, patch: Partial<ExitEvent['correlate'][number]>) => onChange({ ...event, correlate: rows.map((r, k) => (k === j ? { ...r, ...patch } : r)) });
  return (
    <div role="group" aria-label={`Correlation of exit event ${index + 1}`} className="canvas-wizard__correlation">
      <p className="hint">Optional. Only an event about this entry ends it: each event property must equal the value the entry keeps in its context (for example transaction_id equals the context key transaction_id, so another transaction does not end it).</p>
      {rows.map((row, j) => (
        <div key={j} className="audience-filter">
          <Field label={`Event property ${j + 1}`} error={messagesAt(issues, 'exit', 'events', index, 'correlate', j, 'property')[0]}>
            <Input value={row.property} maxLength={255} disabled={readOnly} placeholder="transaction_id" onChange={(e) => setRow(j, { property: e.target.value })} />
          </Field>
          <span aria-hidden="true">equals context</span>
          <Field label={`Context key ${j + 1}`} error={messagesAt(issues, 'exit', 'events', index, 'correlate', j, 'contextKey')[0]}>
            <Input value={row.contextKey} maxLength={255} disabled={readOnly} placeholder="transaction_id" onChange={(e) => setRow(j, { contextKey: e.target.value })} />
          </Field>
          {!readOnly && (
            <Button size="sm" variant="ghost" aria-label={`Remove correlation ${j + 1}`} onClick={() => onChange({ ...event, correlate: rows.filter((_, k) => k !== j) })}>
              Remove
            </Button>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button size="sm" disabled={rows.length >= MAX_CORRELATIONS} onClick={() => onChange({ ...event, correlate: [...rows, { property: '', contextKey: '' }] })}>
          Match a property with the entry context
        </Button>
      )}
    </div>
  );
}

/**
 * Exit criteria of a Canvas (BB-112): exit events (BB-100 triggers) with an optional correlation to the entry context,
 * and an exit audience. An exit event ends the entry at once, even during a Delay.
 */
export function ExitCriteriaForm({ environmentId, exit, issues, readOnly, onChange }: { environmentId: string; exit: Exit; issues: SettingsIssue[]; readOnly: boolean; onChange: (next: Exit) => void }) {
  const triggerIssues = issuesBelow(issues, 'exit');
  // Only custom events carry properties to correlate on: another kind drops the correlation.
  const setEvent = (index: number, next: ExitEvent) => onChange({ ...exit, events: exit.events.map((e, i) => (i === index ? next : e)) });
  return (
    <>
      <fieldset className="audience-group" aria-label="Exit events">
        <legend>Exit events</legend>
        <p className="notice notice--neutral">
          An exit event ends the user&apos;s entry immediately, even during a Delay or while Action Paths waits: their pending steps are cancelled and messages not yet handed to the provider are not sent. Messages already delivered are not recalled. Only events after the user entered count.
        </p>
        {exit.events.length === 0 && <p className="hint">No exit events: users leave only at the end of their path or by the exit audience.</p>}
        <ol className="plain-list audience-builder">
          {exit.events.map((event, index) => (
            <li key={index}>
              {index > 0 && (
                <p className="hint" aria-hidden="true">
                  or
                </p>
              )}
              <TriggerEditor
                label={`Exit event ${index + 1}`}
                spec={event.trigger}
                environmentId={environmentId}
                readOnly={readOnly}
                issues={triggerIssues}
                path={['events', index, 'trigger']}
                onChange={(trigger) => setEvent(index, { trigger, correlate: trigger.kind === 'custom_event' ? event.correlate : [] })}
                onRemove={() => onChange({ ...exit, events: exit.events.filter((_, i) => i !== index) })}
              />
              {event.trigger.kind === 'custom_event' && <Correlation event={event} index={index} issues={issues} readOnly={readOnly} onChange={(next) => setEvent(index, next)} />}
            </li>
          ))}
        </ol>
        {!readOnly && (
          <Button size="sm" disabled={exit.events.length >= MAX_EXIT_EVENTS} onClick={() => onChange({ ...exit, events: [...exit.events, { trigger: triggerTemplate('custom_event'), correlate: [] }] })}>
            Add an exit event
          </Button>
        )}
      </fieldset>
      <fieldset className="audience-group" aria-label="Exit audience">
        <legend>Exit audience</legend>
        <RadioGroup
          label="Exit audience"
          options={AUDIENCE_CHOICES.map((o) => ({ ...o, disabled: readOnly }))}
          value={exit.audience ? 'audience' : 'none'}
          onValueChange={(choice) => onChange({ ...exit, audience: choice === 'audience' ? (exit.audience ?? emptyAudience()) : null })}
        />
        <p className="hint">Checked before each step on the user&apos;s current data: a user who matches leaves at their next step and gets no further messages.</p>
        {messagesAt(issues, 'exit', 'audience').map((message) => (
          <p key={message} className="bb-field__error" role="alert">
            {message}
          </p>
        ))}
        {exit.audience && <AudienceBuilder environmentId={environmentId} value={exit.audience} readOnly={readOnly} onChange={(audience) => onChange({ ...exit, audience })} />}
      </fieldset>
    </>
  );
}

export function ExitStep({ environmentId, settings, onChange, issues, readOnly }: WizardStepProps) {
  return (
    <section aria-label="Exit" className="canvas-wizard__panel">
      <h2>Exit criteria</h2>
      <p className="hint">Users leave the Canvas early when they do an exit event or come to match the exit audience.</p>
      <ExitCriteriaForm environmentId={environmentId} exit={settings.exit} issues={issues} readOnly={readOnly} onChange={(exit) => onChange({ ...settings, exit })} />
    </section>
  );
}
